import React from "react";
import { AdminUser } from "../../api/fetchAdminUsers";
import AdminUserActionMenu from "../AdminUserActionMenu/AdminUserActionMenu";
import { Column } from "@/components/Datatable/types";
import { CheckCircle2, XCircle, Minus, Loader2 } from "lucide-react";

interface CreateAdminUserColumnsProps {
  handleEdit: (uuid: string) => void;
  handleImpersonateUser: (employeeUuid: string) => void;
  handleViewSyncLogs: (user: AdminUser) => void;
  handleSyncUser: (user: AdminUser) => void;
  syncingEmployeeIds: number[];
}

const SyncStatusIcon: React.FC<{ appResult: any; isLoading?: boolean }> = ({
  appResult,
  isLoading,
}) => {
  // Show spinner if this app hasn't completed yet but sync is in progress
  if (isLoading && !appResult) {
    return <Loader2 className="h-4 w-4 text-yellow-500 animate-spin" />;
  }

  if (!appResult) {
    return <Minus className="h-4 w-4 text-gray-400" />;
  }

  const success = appResult.response?.success || appResult.response?.Success;

  if (success) {
    return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  }

  return <XCircle className="h-4 w-4 text-red-500" />;
};

export function createAdminUserColumns({
  handleEdit,
  handleImpersonateUser,
  handleViewSyncLogs,
  handleSyncUser,
  syncingEmployeeIds,
}: CreateAdminUserColumnsProps): Column<AdminUser>[] {
  return [
    {
      header: "Employee ID",
      accessorKey: "employeeId",
    },
    {
      header: "First Name",
      accessorKey: "firstName",
      enableSorting: true,
    },
    {
      header: "Last Name",
      accessorKey: "lastName",
      enableSorting: true,
    },
    {
      header: "Email",
      accessorKey: "email",
      enableSorting: true,
    },
    {
      header: "Company",
      accessorKey: "companyName",
      enableSorting: true,
    },
    {
      header: "Role",
      accessorKey: "roleLabel",
      enableSorting: true,
    },
    {
      header: "Salesforce ID",
      accessorKey: "salesforceId",
    },
    {
      id: "syncStatus",
      header: "Sync Status",
      cell: ({ row }) => {
        const user = row.original;
        const syncResults = user.remoteSyncLog?.syncResults;
        const isInProgress =
          user.remoteSyncLog?.status === "in_progress" ||
          (user.employeeId != null &&
            syncingEmployeeIds.includes(user.employeeId));

        return (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1" title="Salesforce">
              <span className="text-xs text-gray-500">SF</span>
              <SyncStatusIcon
                appResult={syncResults?.salesforce}
                isLoading={isInProgress}
              />
            </div>
            <div className="flex items-center gap-1" title="MemberPortal">
              <span className="text-xs text-gray-500">MP</span>
              <SyncStatusIcon
                appResult={syncResults?.memberportal}
                isLoading={isInProgress}
              />
            </div>
            <div className="flex items-center gap-1" title="PartnerNetwork">
              <span className="text-xs text-gray-500">PN</span>
              <SyncStatusIcon
                appResult={syncResults?.partnernetwork}
                isLoading={isInProgress}
              />
            </div>
            <div className="flex items-center gap-1" title="Scoreboard">
              <span className="text-xs text-gray-500">SB</span>
              <SyncStatusIcon
                appResult={syncResults?.scoreboard}
                isLoading={isInProgress}
              />
            </div>
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const userItem = row.original;
        return (
          <AdminUserActionMenu
            employeeUuid={userItem.employeeUuid || ""}
            onEdit={handleEdit}
            onImpersonate={handleImpersonateUser}
            onSyncUser={() => handleSyncUser(userItem)}
            onViewSyncLogs={() => handleViewSyncLogs(userItem)}
          />
        );
      },
    },
  ];
}
